import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
  QuerySnapshot,
  DocumentData,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";

export type QueueTicketType = "normal" | "preferential" | "appointment";
export type QueueTicketStatus = "waiting" | "called" | "done" | "canceled";

export interface QueueTicket {
  id: string;
  number: string;
  sequence: number;
  type: QueueTicketType;
  status: QueueTicketStatus;
  dateKey: string;
  userId?: string;
  createdAt?: Date;
  calledAt?: Date;
  deskId?: string;
  displayName?: string;
  scheduledAt?: Date;
  expiresAt?: Date;
}

export interface QueueCall {
  id: string;
  ticketId: string;
  number: string;
  type: QueueTicketType;
  deskId: string;
  display: string;
  calledAt?: Date;
  dateKey: string;
  userId?: string;
  expiresAt?: Date;
}

const COLLECTIONS = {
  counters: "queue_counters",
  tickets: "queue_tickets",
  calls: "queue_calls",
};

const getDateKey = (date = new Date()) =>
  date.toISOString().slice(0, 10).replace(/-/g, "");

const getEndOfDay = (): Timestamp => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return Timestamp.fromDate(end);
};

const mapTimestamp = (value: any): Date | undefined =>
  value?.toDate ? value.toDate() : undefined;

const mapTicket = (docSnap: DocumentData): QueueTicket => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    number: data.number,
    sequence: data.sequence,
    type: data.type,
    status: data.status,
    dateKey: data.dateKey,
    userId: data.userId,
    createdAt: mapTimestamp(data.createdAt),
    calledAt: mapTimestamp(data.calledAt),
    deskId: data.deskId,
    displayName: data.displayName,
    scheduledAt: mapTimestamp(data.scheduledAt),
    expiresAt: mapTimestamp(data.expiresAt),
  };
};

const mapCall = (docSnap: DocumentData): QueueCall => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ticketId: data.ticketId,
    number: data.number,
    type: data.type,
    deskId: data.deskId,
    display: data.display,
    calledAt: mapTimestamp(data.calledAt),
    dateKey: data.dateKey,
    userId: data.userId,
    expiresAt: mapTimestamp(data.expiresAt),
  };
};

export const generateTicket = async (type: QueueTicketType, userId: string): Promise<QueueTicket> => {
  const dateKey = getDateKey();
  const counterRef = doc(db, COLLECTIONS.counters, `daily-queue-${userId}-${dateKey}`);

  const ticket = await runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(counterRef);
    const data = counterSnap.exists()
      ? counterSnap.data()
      : { normalSeq: 0, preferentialSeq: 0, dateKey, userId };

    const nextSeq =
      (type === "preferential" ? data.preferentialSeq : data.normalSeq) + 1;

    transaction.set(counterRef, {
      dateKey,
      userId,
      normalSeq: type === "normal" ? nextSeq : data.normalSeq,
      preferentialSeq: type === "preferential" ? nextSeq : data.preferentialSeq,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    const number = String(nextSeq).padStart(3, "0");
    const expiresAt = getEndOfDay();

    const ticketRef = doc(collection(db, COLLECTIONS.tickets));
    transaction.set(ticketRef, {
      number,
      sequence: nextSeq,
      type,
      status: "waiting" as QueueTicketStatus,
      dateKey,
      userId,
      createdAt: serverTimestamp(),
      expiresAt,
    });

    return {
      id: ticketRef.id,
      number,
      sequence: nextSeq,
      type,
      status: "waiting",
      dateKey,
      userId,
      expiresAt: expiresAt.toDate(),
    } as QueueTicket;
  });

  return ticket;
};

// Filtra userId + dateKey + status diretamente na query (equality-only, sem índice composto)
export const callNextTicket = async (
  deskId: string,
  userId: string,
  type?: QueueTicketType
): Promise<QueueTicket | null> => {
  const dateKey = getDateKey();

  const snap = await getDocs(query(
    collection(db, COLLECTIONS.tickets),
    where("userId", "==", userId),
    where("dateKey", "==", dateKey),
    where("status", "==", "waiting")
  ));

  const waiting = snap.docs
    .map(mapTicket)
    .sort((a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0));

  const ticket = type
    ? waiting.find((t) => t.type === type) ?? null
    : waiting.find((t) => t.type === "preferential") ??
      waiting.find((t) => t.type === "normal") ??
      null;

  if (!ticket) return null;

  return commitCall(ticket, deskId, userId, dateKey);
};

// Chama uma senha específica escolhida manualmente na lista de espera, fora da ordem/prioridade padrão
export const callTicketById = async (
  ticketId: string,
  deskId: string,
  userId: string
): Promise<QueueTicket | null> => {
  const dateKey = getDateKey();
  const ticketRef = doc(db, COLLECTIONS.tickets, ticketId);
  const snap = await getDoc(ticketRef);

  if (!snap.exists()) return null;

  const ticket = mapTicket(snap);
  if (ticket.status !== "waiting" || ticket.userId !== userId) return null;

  return commitCall(ticket, deskId, userId, dateKey, ticketRef);
};

const commitCall = async (
  ticket: QueueTicket,
  deskId: string,
  userId: string,
  dateKey: string,
  ticketRef = doc(db, COLLECTIONS.tickets, ticket.id)
): Promise<QueueTicket> => {
  await updateDoc(ticketRef, {
    status: "called",
    deskId,
    calledAt: serverTimestamp(),
  });

  await addDoc(collection(db, COLLECTIONS.calls), {
    ticketId: ticket.id,
    number: ticket.number,
    type: ticket.type,
    deskId,
    display: `${ticket.type === "preferential" ? "R" : "E"}-${ticket.number}`,
    calledAt: serverTimestamp(),
    dateKey,
    userId,
    expiresAt: getEndOfDay(),
  });

  return { ...ticket, status: "called", deskId };
};

// Escuta todas as senhas aguardando do usuário, ordenadas por chegada, para permitir escolha manual
export const subscribeToWaitingTickets = (
  userId: string,
  callback: (tickets: QueueTicket[]) => void,
  onError?: (error: Error) => void
) => {
  const dateKey = getDateKey();
  const q = query(
    collection(db, COLLECTIONS.tickets),
    where("userId", "==", userId),
    where("dateKey", "==", dateKey),
    where("status", "==", "waiting")
  );

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot<DocumentData>) => {
      const tickets = snapshot.docs
        .map(mapTicket)
        .sort((a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0));
      callback(tickets);
    },
    (error) => {
      console.error("❌ Erro ao ouvir senhas em espera:", error);
      if (onError) onError(error as Error);
    }
  );
};

// Escuta todos os calls do usuário e ordena client-side (sem índice composto)
export const subscribeToCalls = (
  maxItems: number,
  userId: string,
  callback: (calls: QueueCall[]) => void,
  onError?: (error: Error) => void
) => {
  const dateKey = getDateKey();
  const q = query(
    collection(db, COLLECTIONS.calls),
    where("userId", "==", userId),
    where("dateKey", "==", dateKey)
  );

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot<DocumentData>) => {
      const calls = snapshot.docs
        .map(mapCall)
        .sort((a, b) => (b.calledAt?.getTime() ?? 0) - (a.calledAt?.getTime() ?? 0))
        .slice(0, maxItems);
      callback(calls);
    },
    (error) => {
      console.error("❌ Erro ao ouvir chamadas:", error);
      if (onError) onError(error as Error);
    }
  );
};

// Escuta todos os tickets do usuário e conta client-side (sem índice composto)
export const subscribeToWaitingCount = (
  type: QueueTicketType,
  userId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void
) => {
  const dateKey = getDateKey();
  const q = query(
    collection(db, COLLECTIONS.tickets),
    where("userId", "==", userId),
    where("dateKey", "==", dateKey),
    where("status", "==", "waiting"),
    where("type", "==", type)
  );

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot<DocumentData>) => {
      callback(snapshot.size);
    },
    (error) => {
      console.error("❌ Erro ao ouvir filas:", error);
      if (onError) onError(error as Error);
    }
  );
};

export const cleanupExpiredTickets = async (userId: string): Promise<void> => {
  try {
    const nowTs = Timestamp.fromDate(new Date());

    const [ticketsSnap, callsSnap] = await Promise.all([
      getDocs(query(
        collection(db, COLLECTIONS.tickets),
        where("userId", "==", userId),
        where("expiresAt", "<", nowTs)
      )),
      getDocs(query(
        collection(db, COLLECTIONS.calls),
        where("userId", "==", userId),
        where("expiresAt", "<", nowTs)
      )),
    ]);

    const batch = writeBatch(db);
    let count = 0;

    ticketsSnap.docs.forEach((d) => { batch.delete(d.ref); count++; });
    callsSnap.docs.forEach((d) => { batch.delete(d.ref); count++; });

    if (count > 0) await batch.commit();
  } catch (error) {
    console.error("Erro ao limpar tickets expirados:", error);
  }
};

export const resetDailyQueue = async (userId: string): Promise<void> => {
  const dateKey = getDateKey();

  const [ticketsSnap, callsSnap] = await Promise.all([
    getDocs(query(
      collection(db, COLLECTIONS.tickets),
      where("userId", "==", userId),
      where("dateKey", "==", dateKey)
    )),
    getDocs(query(
      collection(db, COLLECTIONS.calls),
      where("userId", "==", userId),
      where("dateKey", "==", dateKey)
    )),
  ]);

  const batch = writeBatch(db);

  ticketsSnap.docs.forEach((d) => batch.delete(d.ref));
  callsSnap.docs.forEach((d) => batch.delete(d.ref));

  batch.set(doc(db, COLLECTIONS.counters, `daily-queue-${userId}-${dateKey}`), {
    dateKey,
    userId,
    normalSeq: 0,
    preferentialSeq: 0,
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
};
